import React, { useState, useCallback, useEffect } from 'react'
import moment from 'moment'
import { InputField, Select } from '@enamelai/design-system'
import './FormGrid.css'
import { Patient } from '@dataTypes/PatientInfo.types'
import AddressFields from './AddressFields.component'
import {
  genderOptions,
  maritalStatusOptions,
  titleOptions,
} from '@constants/Common/personalInfo.constants'

type PersonalInfoStepProps = {
  patientData: Patient
  handleInputChange: (
    field: keyof Patient,
    value: string | number | boolean | string[] | File[] | undefined,
  ) => void
  validationErrors: { [key: string]: string }
}

type PhoneField = 'cellphone' | 'home_phone' | 'work_phone' | 'fax_number'

const DOB_DISPLAY_FORMAT = 'MM / DD / YYYY'

const toSelectOptions = (
  values: readonly string[],
): { value: string; label: string }[] =>
  values.map((value) => ({ value, label: value }))

const TITLE_OPTIONS = toSelectOptions(titleOptions)
const GENDER_OPTIONS = toSelectOptions(genderOptions)
const MARITAL_STATUS_OPTIONS = toSelectOptions(maritalStatusOptions)

const formatDateOfBirth = (value?: string | null): string =>
  value ? moment.utc(value).format(DOB_DISPLAY_FORMAT) : ''

/** Saved numbers may carry a country prefix ("+1 (201) ..."); the DS phone input expects the national number. */
const toNationalPhone = (phone?: string | null): string => {
  const trimmed = phone?.trim() ?? ''
  if (!trimmed.startsWith('+')) return trimmed
  return trimmed.split(' ').slice(1).join('')
}

const isFutureDate = (date: Date): boolean =>
  moment(date).isAfter(moment(), 'day')

const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  patientData,
  handleInputChange,
  validationErrors,
}) => {
  const [phoneErrors, setPhoneErrors] = useState<
    Partial<Record<PhoneField, string>>
  >({})
  const [dobText, setDobText] = useState(() =>
    formatDateOfBirth(patientData.date_of_birth),
  )

  useEffect(() => {
    setDobText(formatDateOfBirth(patientData.date_of_birth))
  }, [patientData.date_of_birth])

  const validatePhone = useCallback((phone: string, field: PhoneField) => {
    const digits = phone.replace(/\D/g, '')
    const isInvalid =
      digits.length !== 10 && (field === 'cellphone' || digits.length > 0)
    setPhoneErrors((prev) => ({
      ...prev,
      [field]: isInvalid ? 'Phone number must be exactly 10 digits' : undefined,
    }))
  }, [])

  const handlePhoneChange = (value: string, field: PhoneField): void => {
    handleInputChange(field, value)
    validatePhone(value, field)
  }

  const handleDobTextChange = (value: string): void => {
    setDobText(value)
    if (!value.trim()) handleInputChange('date_of_birth', '')
  }

  const textField = (
    field: keyof Patient,
    label: string,
    placeholder: string,
    options: { optional?: boolean } = {},
  ): React.ReactNode => (
    <InputField
      fullWidth
      variant="text"
      label={label}
      placeholder={placeholder}
      optional={options.optional}
      value={(patientData[field] as string | undefined) ?? ''}
      onChange={(e) => handleInputChange(field, e.target.value)}
      error={!!validationErrors[field]}
      helperText={validationErrors[field] || undefined}
    />
  )

  const phoneField = (
    field: PhoneField,
    label: string,
    options: { optional?: boolean; requiredError?: string } = {},
  ): React.ReactNode => {
    const message = options.requiredError || phoneErrors[field]
    return (
      <InputField
        fullWidth
        variant="phone"
        label={label}
        placeholder="(201) 555-0017"
        optional={options.optional}
        value={toNationalPhone(patientData[field] as string | undefined)}
        onChange={(e) => handlePhoneChange(e.target.value, field)}
        error={!!message}
        helperText={message || undefined}
      />
    )
  }

  return (
    <div className="add-patient-step">
      <div className="form-grid">
        <div className="form-grid__col--2">
          <Select
            label="Title"
            options={TITLE_OPTIONS}
            value={patientData.title || ''}
            onChange={(value) => handleInputChange('title', String(value))}
          />
        </div>
        <div className="form-grid__col--4">
          {textField('first_name', 'First name', 'ex. John')}
        </div>
        <div className="form-grid__col--6">
          {textField('last_name', 'Last name', 'ex. Doe')}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-grid__col--6">
          {textField('middle_name', 'Middle name', 'ex. Knox')}
        </div>
        <div className="form-grid__col--6">
          {textField(
            'preferred_name',
            'Preferred name',
            'Preferred name or N/A',
            { optional: true },
          )}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-grid__col--6">
          <InputField
            fullWidth
            variant="date"
            label="Date of birth"
            value={dobText}
            onChange={(e) => handleDobTextChange(e.target.value)}
            onDateChange={(date) =>
              handleInputChange(
                'date_of_birth',
                moment(date).format('YYYY-MM-DD'),
              )
            }
            isDateDisabled={isFutureDate}
            error={!!validationErrors.date_of_birth}
            helperText={validationErrors.date_of_birth || undefined}
          />
        </div>
        <div className="form-grid__col--3">
          <Select
            label="Gender"
            placeholder="Select"
            options={GENDER_OPTIONS}
            value={patientData.gender || ''}
            onChange={(value) => handleInputChange('gender', String(value))}
            error={!!validationErrors.gender}
            helperText={validationErrors.gender || undefined}
          />
        </div>
        <div className="form-grid__col--3">
          <Select
            label="Marital status"
            placeholder="Select"
            options={MARITAL_STATUS_OPTIONS}
            value={patientData.marital_status || ''}
            onChange={(value) =>
              handleInputChange('marital_status', String(value))
            }
            error={!!validationErrors.marital_status}
            helperText={validationErrors.marital_status || undefined}
          />
        </div>
      </div>

      <AddressFields
        patientData={patientData}
        handleInputChange={handleInputChange}
        validationErrors={validationErrors}
      />

      <div className="form-grid">
        <div className="form-grid__col--6">
          {textField('profession', 'Profession', 'Profession')}
        </div>
        <div className="form-grid__col--6">
          {textField('company_name', 'Company name', 'Company name or N/A')}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-grid__col--6">
          {phoneField('cellphone', 'Cell phone', {
            requiredError:
              validationErrors.cellphone || validationErrors.mobile,
          })}
        </div>
        <div className="form-grid__col--6">
          <InputField
            fullWidth
            variant="email"
            label="Email"
            placeholder="example@email.com"
            value={patientData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!validationErrors.email}
            helperText={validationErrors.email || undefined}
          />
        </div>
      </div>

      <div className="form-grid">
        <div className="form-grid__col--4">
          {phoneField('home_phone', 'Home phone', {
            optional: true,
            requiredError: validationErrors.home_phone,
          })}
        </div>
        <div className="form-grid__col--4">
          {phoneField('work_phone', 'Work phone', {
            optional: true,
            requiredError: validationErrors.work_phone,
          })}
        </div>
        <div className="form-grid__col--4">
          {phoneField('fax_number', 'Fax number', {
            optional: true,
            requiredError: validationErrors.fax_number,
          })}
        </div>
      </div>
    </div>
  )
}

export default React.memo(PersonalInfoStep)
